import React, { useState, useEffect } from 'react';
import { 
  Leaf, 
  Edit, 
  Trash2, 
  Search, 
  Plus, 
  Eye, 
  EyeOff,
  Euro,
  Save,
  X,
  Filter,
  Tag
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import LoadingSpinner from '../LoadingSpinner';
import { adminAPI, ingredientsAPI } from '../../services/api';
import FileUpload from './FileUpload';
import { getImageUrlWithFallback } from '../../utils/imageUtils';

const AdminIngredients = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    available: '',
    search: ''
  });
  
  // Liquor category configuration state
  const [categoryConfig, setCategoryConfig] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableLiquors, setAvailableLiquors] = useState([]);
  const [showCategoryConfig, setShowCategoryConfig] = useState(false);
  const [categoryConfigLoading, setCategoryConfigLoading] = useState(false);

  // Ingredient categories management state
  const [ingredientCategories, setIngredientCategories] = useState([]);
  const [showCategoriesManagement, setShowCategoriesManagement] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    id: '',
    name: '',
    description: '',
    color: '#10b981',
    icon: '🏷️',
    sort_order: 0
  });
  
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [ingredientForm, setIngredientForm] = useState({
    id: '',
    name: '',
    description: '',
    price: '',
    category: 'botanicals',
    image: '',
    available: true,
    seasonal: false,
    origin: '',
    harvestSeason: '',
    flavorProfile: '',
    alcoholSoluble: true,
    waterSoluble: false,
    extractionTime: '',
    storageRequirements: '',
    allergens: '',
    organicCertified: false,
    allowed_product_ids: []
  });

  useEffect(() => {
    loadIngredients();
  }, [filters]);

  useEffect(() => {
    loadIngredients();
    loadIngredientCategories(); // Load categories for dropdown
    loadLiquorTypes(); // Popola availableLiquors per il multi-select "Prodotti esclusivi"
  }, [adminToken]);

  const loadIngredients = async () => {
    try {
      setLoading(true);
      
      // Call real ingredients API
      const response = await adminAPI.getIngredients(filters, 1, 50);
      if (response.success) {
        setIngredients(response.ingredients);
      } else {
        throw new Error(response.message || 'Failed to load ingredients');
      }
      
    } catch (error) {
      console.error('Error loading ingredients:', error);
      toast({
        title: "Errore nel caricamento",
        description: "Impossibile caricare gli ingredienti",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Load available liquor types
  const loadLiquorTypes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/liquor-types`);
      if (response.ok) {
        const liquors = await response.json();
        setAvailableLiquors(liquors || []);
      } else {
        console.warn('Failed to load liquor types, using defaults');
        setAvailableLiquors([
          { id: 'gin', name: 'Gin' },
          { id: 'amaro', name: 'Amaro' },
          { id: 'bitter', name: 'Bitter' }
        ]);
      }
    } catch (error) {
      console.error('Error loading liquor types:', error);
      setAvailableLiquors([
        { id: 'gin', name: 'Gin' },
        { id: 'amaro', name: 'Amaro' },
        { id: 'bitter', name: 'Bitter' }
      ]);
    }
  };

  // Load liquor category configurations
  const loadCategoryConfig = async () => {
    try {
      setCategoryConfigLoading(true);
      
      // Load liquor types first
      await loadLiquorTypes();
      
      const response = await ingredientsAPI.getLiquorCategoryConfig(adminToken);
      if (response.success) {
        setCategoryConfig(response.configurations);
        setAvailableCategories(response.available_categories);
      } else {
        throw new Error(response.message || 'Failed to load category config');
      }
    } catch (error) {
      console.error('Error loading category config:', error);
      toast({
        title: "Errore caricamento configurazione",
        description: "Impossibile caricare la configurazione delle categorie",
        variant: "destructive"
      });
    } finally {
      setCategoryConfigLoading(false);
    }
  };

  // Update liquor category configuration
  const updateCategoryConfig = async (liquorType, enabledCategories) => {
    try {
      const response = await ingredientsAPI.updateLiquorCategoryConfig(adminToken, {
        liquor_type: liquorType,
        enabled_categories: enabledCategories
      });
      
      if (response.success) {
        toast({
          title: "Configurazione aggiornata",
          description: `Categorie per ${liquorType} aggiornate con successo`,
        });
        loadCategoryConfig(); // Reload to get updated data
      } else {
        throw new Error(response.message || 'Failed to update category config');
      }
    } catch (error) {
      console.error('Error updating category config:', error);
      toast({
        title: "Errore aggiornamento",
        description: error.message || "Impossibile aggiornare la configurazione",
        variant: "destructive"
      });
    }
  };

  // Toggle category for a liquor type
  const toggleCategory = (liquorType, categoryId) => {
    const currentConfig = categoryConfig[liquorType] || { enabled_categories: [] };
    const currentCategories = currentConfig.enabled_categories || [];
    
    let newCategories;
    if (currentCategories.includes(categoryId)) {
      // Remove category
      newCategories = currentCategories.filter(cat => cat !== categoryId);
    } else {
      // Add category
      newCategories = [...currentCategories, categoryId];
    }
    
    updateCategoryConfig(liquorType, newCategories);
  };

  // Load ingredient categories
  const loadIngredientCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await ingredientsAPI.getIngredientCategories(adminToken);
      if (response.success) {
        setIngredientCategories(response.categories);
      } else {
        throw new Error(response.message || 'Failed to load categories');
      }
    } catch (error) {
      console.error('Error loading ingredient categories:', error);
      toast({
        title: "Errore caricamento categorie",
        description: "Impossibile caricare le categorie degli ingredienti",
        variant: "destructive"
      });
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Create ingredient category
  const createIngredientCategory = async (categoryData) => {
    try {
      const response = await ingredientsAPI.createIngredientCategory(adminToken, categoryData);
      if (response.success) {
        toast({
          title: "Categoria creata",
          description: "Nuova categoria creata con successo",
        });
        loadIngredientCategories();
        setShowCreateCategory(false);
        resetCategoryForm();
      } else {
        throw new Error(response.message || 'Failed to create category');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: "Errore creazione categoria",
        description: error.message || "Impossibile creare la categoria",
        variant: "destructive"
      });
    }
  };

  // Update ingredient category
  const updateIngredientCategory = async (categoryId, categoryData) => {
    try {
      const response = await ingredientsAPI.updateIngredientCategory(adminToken, categoryId, categoryData);
      if (response.success) {
        toast({
          title: "Categoria aggiornata",
          description: "Categoria aggiornata con successo",
        });
        loadIngredientCategories();
        setSelectedCategory(null);
        resetCategoryForm();
      } else {
        throw new Error(response.message || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Errore aggiornamento categoria",
        description: error.message || "Impossibile aggiornare la categoria",
        variant: "destructive"
      });
    }
  };

  // Delete ingredient category
  const deleteIngredientCategory = async (categoryId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa categoria? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      const response = await ingredientsAPI.deleteIngredientCategory(adminToken, categoryId);
      if (response.success) {
        toast({
          title: "Categoria eliminata",
          description: "Categoria eliminata con successo",
        });
        loadIngredientCategories();
      } else {
        throw new Error(response.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Errore eliminazione categoria",
        description: error.message || "Impossibile eliminare la categoria",
        variant: "destructive"
      });
    }
  };

  // Toggle ingredient category active status
  const toggleIngredientCategoryActive = async (categoryId) => {
    try {
      const response = await ingredientsAPI.toggleIngredientCategoryActive(adminToken, categoryId);
      if (response.success) {
        toast({
          title: "Stato categoria aggiornato",
          description: response.message,
        });
        loadIngredientCategories();
      } else {
        throw new Error(response.message || 'Failed to toggle category');
      }
    } catch (error) {
      console.error('Error toggling category:', error);
      toast({
        title: "Errore cambio stato categoria",
        description: error.message || "Impossibile cambiare lo stato della categoria",
        variant: "destructive"
      });
    }
  };

  // Reset category form
  const resetCategoryForm = () => {
    setCategoryForm({
      id: '',
      name: '',
      description: '',
      color: '#10b981',
      icon: '🏷️',
      sort_order: 0
    });
  };

  // Open edit category modal
  const openEditCategory = (category) => {
    setSelectedCategory(category);
    setCategoryForm({
      id: category.id,
      name: category.name,
      description: category.description || '',
      color: category.color || '#10b981',
      icon: category.icon || '🏷️',
      sort_order: category.sort_order || 0
    });
    setShowCreateCategory(true);
  };

  const handleUpdateIngredient = async (ingredientId, updateData) => {
    try {
      setUpdating(ingredientId);
      
      // Clean and format data for backend
      const cleanedData = {
        name: updateData.name,
        description: updateData.description,
        price: parseFloat(updateData.price),
        category: updateData.category,
        available: updateData.available
      };
      
      // Add optional fields only if they have values
      if (updateData.image && updateData.image !== '') {
        cleanedData.image = updateData.image;
      }
      if (updateData.origin && updateData.origin !== '') {
        cleanedData.origin = updateData.origin;
      }
      if (updateData.intensity && updateData.intensity !== '') {
        cleanedData.intensity = parseInt(updateData.intensity);
      }
      if (updateData.allergens && updateData.allergens !== '') {
        cleanedData.allergens = updateData.allergens.split(',').map(a => a.trim());
      }
      if (updateData.seasonal !== undefined) {
        cleanedData.seasonal = updateData.seasonal;
      }
      if (updateData.organicCertified !== undefined) {
        cleanedData.organicCertified = updateData.organicCertified;
      }
      if (updateData.alcoholSoluble !== undefined) {
        cleanedData.alcoholSoluble = updateData.alcoholSoluble;
      }
      if (updateData.waterSoluble !== undefined) {
        cleanedData.waterSoluble = updateData.waterSoluble;
      }
      if (updateData.allowed_product_ids !== undefined) {
        cleanedData.allowed_product_ids = Array.isArray(updateData.allowed_product_ids) ? updateData.allowed_product_ids : [];
      }
      
      const response = await adminAPI.updateIngredient(ingredientId, cleanedData);
      if (response.success) {
        toast({
          title: "Ingrediente aggiornato",
          description: "Le modifiche sono state salvate con successo",
        });
        
        setShowEditModal(false);
        setSelectedIngredient(null);
        loadIngredients();
      } else {
        throw new Error(response.message || 'Failed to update ingredient');
      }
      
    } catch (error) {
      console.error('Error updating ingredient:', error);
      toast({
        title: "Errore nell'aggiornamento",
        description: error.message || "Impossibile aggiornare l'ingrediente",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleCreateIngredient = async (ingredientData) => {
    try {
      setUpdating('creating');
      
      // Clean and format data for backend
      const cleanedData = {
        name: ingredientData.name,
        description: ingredientData.description,
        price: parseFloat(ingredientData.price),
        category: ingredientData.category,
        available: ingredientData.available
      };
      
      // Add optional fields only if they have values
      if (ingredientData.image && ingredientData.image !== '') {
        cleanedData.image = ingredientData.image;
      }
      if (ingredientData.origin && ingredientData.origin !== '') {
        cleanedData.origin = ingredientData.origin;
      }
      if (ingredientData.intensity && ingredientData.intensity !== '') {
        cleanedData.intensity = parseInt(ingredientData.intensity);
      }
      if (ingredientData.allergens && ingredientData.allergens !== '') {
        cleanedData.allergens = ingredientData.allergens.split(',').map(a => a.trim());
      }
      if (ingredientData.seasonal !== undefined) {
        cleanedData.seasonal = ingredientData.seasonal;
      }
      if (ingredientData.organicCertified !== undefined) {
        cleanedData.organicCertified = ingredientData.organicCertified;
      }
      if (ingredientData.alcoholSoluble !== undefined) {
        cleanedData.alcoholSoluble = ingredientData.alcoholSoluble;
      }
      if (ingredientData.waterSoluble !== undefined) {
        cleanedData.waterSoluble = ingredientData.waterSoluble;
      }
      if (Array.isArray(ingredientData.allowed_product_ids) && ingredientData.allowed_product_ids.length > 0) {
        cleanedData.allowed_product_ids = ingredientData.allowed_product_ids;
      }
      
      const response = await adminAPI.createIngredient(cleanedData);
      if (response.success) {
        toast({
          title: "Ingrediente creato",
          description: `Ingrediente "${ingredientData.name}" creato con successo`,
        });
        
        setShowCreateModal(false);
        resetForm();
        loadIngredients();
      } else {
        throw new Error(response.message || 'Failed to create ingredient');
      }
      
    } catch (error) {
      console.error('Error creating ingredient:', error);
      toast({
        title: "Errore nella creazione",
        description: error.message || "Impossibile creare l'ingrediente",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteIngredient = async (ingredientId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo ingrediente? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      setUpdating(ingredientId);
      
      const response = await adminAPI.deleteIngredient(ingredientId);
      if (response.success) {
        toast({
          title: "Ingrediente eliminato",
          description: "L'ingrediente è stato eliminato dal catalogo",
        });
        
        loadIngredients();
      } else {
        throw new Error(response.message || 'Failed to delete ingredient');
      }
      
    } catch (error) {
      console.error('Error deleting ingredient:', error);
      toast({
        title: "Errore nell'eliminazione",
        description: error.message || "Impossibile eliminare l'ingrediente",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const toggleAvailability = async (ingredientId, currentAvailability) => {
    try {
      setUpdating(ingredientId);
      
      const response = await adminAPI.updateIngredient(ingredientId, { available: !currentAvailability });
      if (response.success) {
        toast({
          title: "Disponibilità aggiornata",
          description: `Ingrediente ${!currentAvailability ? 'attivato' : 'disattivato'}`,
        });
        
        loadIngredients();
      } else {
        throw new Error(response.message || 'Failed to update availability');
      }
      
    } catch (error) {
      console.error('Error toggling availability:', error);
      toast({
        title: "Errore",
        description: error.message || "Impossibile aggiornare la disponibilità",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const getCategoryColor = (categoryId) => {
    const category = ingredientCategories.find(cat => cat.id === categoryId);
    if (category && category.color) {
      // Convert hex color to background and text classes
      const colorValue = category.color;
      return `text-white`; // Simple approach - use text white for all colored backgrounds
    }
    
    // Fallback colors for unknown categories
    const fallbackColors = {
      'botanicals': 'bg-green-100 text-green-800',
      'citrus': 'bg-orange-100 text-orange-800',
      'spices': 'bg-red-100 text-red-800',
      'ricette-distillatore': 'bg-amber-100 text-amber-800'
    };
    return fallbackColors[categoryId] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryName = (categoryId) => {
    const category = ingredientCategories.find(cat => cat.id === categoryId);
    if (category) {
      return `${category.icon} ${category.name}`;
    }
    
    // Fallback names for unknown categories
    const fallbackNames = {
      'botanicals': '🌿 Botaniche',
      'citrus': '🍊 Agrumi',
      'spices': '🌶️ Spezie',
      'ricette-distillatore': '🧪 Ricette dal Distillatore'
    };
    return fallbackNames[categoryId] || categoryId;
  };

  const openEditModal = (ingredient) => {
    setSelectedIngredient(ingredient);
    setIngredientForm({
      id: ingredient.id || '',
      name: ingredient.name || '',
      description: ingredient.description || '',
      price: ingredient.price?.toString() || '',
      category: ingredient.category || 'botanicals',
      available: ingredient.available !== undefined ? ingredient.available : true,
      seasonal: ingredient.seasonal || false,
      origin: ingredient.origin || '',
      harvestSeason: ingredient.harvestSeason || '',
      flavorProfile: ingredient.flavorProfile || '',
      alcoholSoluble: ingredient.alcoholSoluble !== undefined ? ingredient.alcoholSoluble : true,
      waterSoluble: ingredient.waterSoluble || false,
      extractionTime: ingredient.extractionTime || '',
      storageRequirements: ingredient.storageRequirements || '',
      allergens: ingredient.allergens || '',
      organicCertified: ingredient.organicCertified || false,
      allowed_product_ids: Array.isArray(ingredient.allowed_product_ids) ? ingredient.allowed_product_ids : []
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setIngredientForm({
      id: '',
      name: '',
      description: '',
      price: '',
      category: 'botanicals',
      available: true,
      seasonal: false,
      origin: '',
      harvestSeason: '',
      flavorProfile: '',
      alcoholSoluble: true,
      waterSoluble: false,
      extractionTime: '',
      storageRequirements: '',
      allergens: '',
      organicCertified: false,
      allowed_product_ids: []
    });
  };

  const handleInputChange = (field, value) => {
    setIngredientForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredIngredients = ingredients.filter(ingredient => {
    const matchesSearch = !filters.search || 
      ingredient.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      ingredient.description.toLowerCase().includes(filters.search.toLowerCase()) ||
      ingredient.category.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesCategory = !filters.category || ingredient.category === filters.category;
    const matchesAvailability = filters.available === '' || ingredient.available.toString() === filters.available;
    
    return matchesSearch && matchesCategory && matchesAvailability;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestione Ingredienti</h1>
            <p className="text-gray-600 mt-1">
              Gestisci botaniche, spezie e altri ingredienti per i liquori personalizzati
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => {
                setShowCategoriesManagement(true);
                loadIngredientCategories();
              }}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Tag size={20} />
              Gestisci Categorie
            </button>
            <button
              onClick={() => {
                setShowCategoryConfig(true);
                if (Object.keys(categoryConfig).length === 0) {
                  loadCategoryConfig();
                }
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Filter size={20} />
              Categorie per Liquore
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <Plus size={20} />
              Nuovo Ingrediente
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cerca
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Nome, descrizione, categoria..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria
              </label>
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Tutte le categorie</option>
                {ingredientCategories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Disponibilità
              </label>
              <select
                value={filters.available}
                onChange={(e) => setFilters(prev => ({ ...prev, available: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Tutti</option>
                <option value="true">Disponibili</option>
                <option value="false">Non disponibili</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ category: '', available: '', search: '' })}
                className="w-full px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset Filtri
              </button>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Totale Ingredienti</p>
                <p className="text-2xl font-bold text-gray-900">{ingredients.length}</p>
              </div>
              <Leaf className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Disponibili</p>
                <p className="text-2xl font-bold text-green-600">
                  {ingredients.filter(ing => ing.available).length}
                </p>
              </div>
              <Eye className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Non Disponibili</p>
                <p className="text-2xl font-bold text-red-600">
                  {ingredients.filter(ing => !ing.available).length}
                </p>
              </div>
              <EyeOff className="text-red-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Categorie</p>
                <p className="text-2xl font-bold text-purple-600">
                  {new Set(ingredients.map(ing => ing.category)).size}
                </p>
              </div>
              <Filter className="text-purple-500" size={24} />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {/* Ingredients Table */}
        {!loading && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Immagine</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Ingrediente</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Categoria</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Prezzo</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Origine</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Disponibilità</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredIngredients.map((ingredient) => (
                    <tr key={ingredient.id} className="hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div className="w-12 h-12 overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center">
                          {ingredient.image ? (
                            <img 
                              src={getImageUrlWithFallback(ingredient.image, 'ingredients', ingredient.id)} 
                              alt={ingredient.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <Leaf className="w-6 h-6 text-gray-400" />
                          )}
                          <div className="w-full h-full items-center justify-center hidden">
                            <Leaf className="w-6 h-6 text-gray-400" />
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <h3 className="font-medium text-gray-900 flex items-center gap-2">
                            {ingredient.name}
                            {Array.isArray(ingredient.allowed_product_ids) && ingredient.allowed_product_ids.length > 0 && (
                              <span
                                title={`Esclusivo per ${ingredient.allowed_product_ids.length} prodotto/i`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-bold uppercase tracking-wide"
                                data-testid={`badge-exclusive-${ingredient.id}`}
                              >
                                🔒 Esclusivo ×{ingredient.allowed_product_ids.length}
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {ingredient.description?.substring(0, 60)}...
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(ingredient.category)}`}>
                          {getCategoryName(ingredient.category)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1">
                          <Euro size={14} className="text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {ingredient.price?.toFixed(2)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600">
                          {ingredient.origin || 'Non specificato'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <button
                          onClick={() => toggleAvailability(ingredient.id, ingredient.available)}
                          disabled={updating === ingredient.id}
                          className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            ingredient.available
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          } ${updating === ingredient.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {updating === ingredient.id ? (
                            <LoadingSpinner size="xs" />
                          ) : ingredient.available ? (
                            <Eye size={12} />
                          ) : (
                            <EyeOff size={12} />
                          )}
                          {ingredient.available ? 'Disponibile' : 'Non disponibile'}
                        </button>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(ingredient)}
                            disabled={updating === ingredient.id}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Modifica ingrediente"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteIngredient(ingredient.id)}
                            disabled={updating === ingredient.id}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Elimina ingrediente"
                          >
                            {updating === ingredient.id ? (
                              <LoadingSpinner size="xs" />
                            ) : (
                              <Trash2 size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredIngredients.length === 0 && (
              <div className="text-center py-12">
                <Leaf className="mx-auto text-gray-400 mb-4" size={48} />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun ingrediente trovato</h3>
                <p className="text-gray-600 mb-4">
                  {filters.search || filters.category || filters.available 
                    ? 'Prova a modificare i filtri di ricerca.' 
                    : 'Inizia aggiungendo il tuo primo ingrediente.'}
                </p>
                {!filters.search && !filters.category && !filters.available && (
                  <button
                    onClick={() => {
                      resetForm();
                      setShowCreateModal(true);
                    }}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Crea Primo Ingrediente
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Modal */}
        {(showCreateModal || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {showCreateModal ? 'Crea Nuovo Ingrediente' : 'Modifica Ingrediente'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedIngredient(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Ingrediente *
                    </label>
                    <input
                      type="text"
                      value={ingredientForm.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="es. Ginepro"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoria *
                    </label>
                    <select
                      value={ingredientForm.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    >
                      {ingredientCategories.filter(cat => cat.active).map(category => (
                        <option key={category.id} value={category.id}>
                          {category.icon} {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrizione *
                  </label>
                  <textarea
                    value={ingredientForm.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Descrivi l'ingrediente, le sue caratteristiche e proprietà..."
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prezzo (€) *
                    </label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={ingredientForm.price}
                        onChange={(e) => handleInputChange('price', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Origine
                    </label>
                    <input
                      type="text"
                      value={ingredientForm.origin}
                      onChange={(e) => handleInputChange('origin', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="es. Toscana, Italia"
                    />
                  </div>
                </div>
                
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Immagine Ingrediente
                  </label>
                  
                  {/* Current Image Preview */}
                  {ingredientForm.image && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
                      <p className="text-sm font-medium text-gray-700 mb-2">Immagine attuale:</p>
                      <div className="flex items-center space-x-4">
                        <img 
                          src={getImageUrlWithFallback(ingredientForm.image, 'ingredients', ingredientForm.id)} 
                          alt={ingredientForm.name || 'Anteprima ingrediente'}
                          className="w-20 h-20 object-cover rounded-lg border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-20 h-20 bg-gray-200 rounded-lg border flex items-center justify-center hidden">
                          <Leaf className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">
                            {ingredientForm.image.split('/').pop()}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleInputChange('image', '')}
                            className="text-sm text-red-600 hover:text-red-700 mt-1 inline-flex items-center"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Rimuovi immagine
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className={ingredientForm.image ? 'border-t pt-3' : ''}>
                    <p className="text-sm text-gray-600 mb-2">
                      {ingredientForm.image ? 'Carica una nuova immagine:' : 'Carica immagine:'}
                    </p>
                    <FileUpload
                      category="ingredients"
                      itemId={ingredientForm.id || 'new'}
                      currentImage={ingredientForm.image}
                      onImageUploaded={(imagePath) => 
                        handleInputChange('image', imagePath)
                      }
                    />
                  </div>
                </div>
                
                {/* Options */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="available"
                      checked={ingredientForm.available}
                      onChange={(e) => handleInputChange('available', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="available" className="ml-2 text-sm text-gray-700">
                      Disponibile
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="seasonal"
                      checked={ingredientForm.seasonal}
                      onChange={(e) => handleInputChange('seasonal', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="seasonal" className="ml-2 text-sm text-gray-700">
                      Stagionale
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="alcoholSoluble"
                      checked={ingredientForm.alcoholSoluble}
                      onChange={(e) => handleInputChange('alcoholSoluble', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="alcoholSoluble" className="ml-2 text-sm text-gray-700">
                      Alcolubile
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="organicCertified"
                      checked={ingredientForm.organicCertified}
                      onChange={(e) => handleInputChange('organicCertified', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="organicCertified" className="ml-2 text-sm text-gray-700">
                      Bio Certificato
                    </label>
                  </div>
                </div>
              </div>

              {/* Prodotti esclusivi */}
              <div className="p-6 border-t border-gray-200 bg-purple-50/40" data-testid="allowed-products-section">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800">🔒 Prodotti a cui associare questo ingrediente</label>
                    <p className="text-xs text-gray-600 mt-1">
                      Lascia <strong>vuoto</strong> per rendere l&apos;ingrediente disponibile su TUTTI i prodotti della sua categoria.
                      Seleziona uno o più prodotti per limitarne l&apos;uso solo a quelli.
                    </p>
                  </div>
                  {(ingredientForm.allowed_product_ids || []).length > 0 && (
                    <button
                      type="button"
                      onClick={() => handleInputChange('allowed_product_ids', [])}
                      className="text-xs text-purple-600 hover:text-purple-800 underline whitespace-nowrap"
                      data-testid="btn-reset-allowed-products"
                    >
                      Rimuovi restrizioni
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-52 overflow-y-auto pr-1">
                  {(availableLiquors || []).map(lq => {
                    const selected = (ingredientForm.allowed_product_ids || []).includes(lq.id);
                    return (
                      <label
                        key={lq.id}
                        className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm transition-colors ${selected ? 'bg-purple-600 text-white border-purple-700' : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'}`}
                        data-testid={`allowed-product-${lq.id}`}
                      >
                        <input
                          type="checkbox"
                          className="accent-purple-600"
                          checked={selected}
                          onChange={(e) => {
                            const cur = ingredientForm.allowed_product_ids || [];
                            const next = e.target.checked
                              ? [...new Set([...cur, lq.id])]
                              : cur.filter(x => x !== lq.id);
                            handleInputChange('allowed_product_ids', next);
                          }}
                        />
                        <span className="truncate">{lq.name}</span>
                      </label>
                    );
                  })}
                </div>
                {(ingredientForm.allowed_product_ids || []).length > 0 && (
                  <div className="mt-3 text-xs bg-purple-100 border border-purple-200 rounded px-3 py-2 text-purple-900">
                    ⚡ Ingrediente <strong>esclusivo</strong> — visibile solo per {(ingredientForm.allowed_product_ids || []).length} prodotto/i selezionato/i.
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedIngredient(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={updating}
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    if (showCreateModal) {
                      handleCreateIngredient(ingredientForm);
                    } else {
                      handleUpdateIngredient(selectedIngredient.id, ingredientForm);
                    }
                  }}
                  disabled={updating || !ingredientForm.name || !ingredientForm.description || !ingredientForm.price}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {updating ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Save size={16} />
                  )}
                  {showCreateModal ? 'Crea Ingrediente' : 'Salva Modifiche'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ingredient Categories Management Modal */}
        {showCategoriesManagement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-6xl mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Gestione Categorie Ingredienti</h2>
                <button
                  onClick={() => setShowCategoriesManagement(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex justify-between items-center mb-6">
                <p className="text-gray-600">
                  Gestisci le categorie utilizzate per organizzare gli ingredienti nel Builder.
                </p>
                <button
                  onClick={() => {
                    resetCategoryForm();
                    setSelectedCategory(null);
                    setShowCreateCategory(true);
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Plus size={16} />
                  Nuova Categoria
                </button>
              </div>

              {categoriesLoading ? (
                <LoadingSpinner message="Caricamento categorie..." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {ingredientCategories.map(category => (
                    <div 
                      key={category.id}
                      className={`p-4 rounded-lg border-2 transition-colors ${
                        category.active 
                          ? 'border-green-300 bg-green-50' 
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-2xl p-2 rounded-lg"
                            style={{ backgroundColor: `${category.color}20`, color: category.color }}
                          >
                            {category.icon}
                          </span>
                          <div>
                            <h3 className="font-semibold text-gray-900">{category.name}</h3>
                            <p className="text-xs text-gray-500">ID: {category.id}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => toggleIngredientCategoryActive(category.id)}
                            className={`p-1 rounded ${
                              category.active 
                                ? 'text-green-600 hover:bg-green-100' 
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={category.active ? 'Disattiva' : 'Attiva'}
                          >
                            {category.active ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                          <button
                            onClick={() => openEditCategory(category)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                            title="Modifica"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => deleteIngredientCategory(category.id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="Elimina"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      
                      {category.description && (
                        <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                      )}
                      
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Ordine: {category.sort_order}</span>
                        <span className={`px-2 py-1 rounded ${
                          category.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {category.active ? 'Attiva' : 'Inattiva'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create/Edit Category Modal */}
        {showCreateCategory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedCategory ? 'Modifica Categoria' : 'Nuova Categoria'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateCategory(false);
                    setSelectedCategory(null);
                    resetCategoryForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  if (selectedCategory) {
                    updateIngredientCategory(selectedCategory.id, {
                      name: categoryForm.name,
                      description: categoryForm.description,
                      color: categoryForm.color,
                      icon: categoryForm.icon,
                      sort_order: parseInt(categoryForm.sort_order)
                    });
                  } else {
                    createIngredientCategory(categoryForm);
                  }
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID Categoria *
                    </label>
                    <input
                      type="text"
                      value={categoryForm.id}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, id: e.target.value }))}
                      disabled={selectedCategory !== null}
                      placeholder="es: nuova-categoria"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                      pattern="^[a-z0-9-]+$"
                      title="Solo lettere minuscole, numeri e trattini"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Solo lettere minuscole, numeri e trattini</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Categoria *
                    </label>
                    <input
                      type="text"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="es: Nuova Categoria"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrizione
                  </label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descrizione opzionale della categoria..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Icona
                    </label>
                    <input
                      type="text"
                      value={categoryForm.icon}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, icon: e.target.value }))}
                      placeholder="🏷️"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-center text-xl"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Colore
                    </label>
                    <input
                      type="color"
                      value={categoryForm.color}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ordine
                    </label>
                    <input
                      type="number"
                      value={categoryForm.sort_order}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateCategory(false);
                      setSelectedCategory(null);
                      resetCategoryForm();
                    }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Save size={16} />
                    {selectedCategory ? 'Salva Modifiche' : 'Crea Categoria'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Liquor Category Configuration Modal */}
        {showCategoryConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Configurazione Categorie per Liquore</h2>
                <button
                  onClick={() => setShowCategoryConfig(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <p className="text-gray-600 mb-6">
                Configura quali categorie di ingredienti sono visibili nello Step 2 del Builder per ogni tipo di liquore.
              </p>

              {categoryConfigLoading ? (
                <LoadingSpinner message="Caricamento configurazione..." />
              ) : (
                <div className="space-y-6">
                  {availableLiquors.map(liquor => (
                    <div key={liquor.id} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {liquor.name}
                        <span className="text-sm font-normal text-gray-500 ml-2">({liquor.id})</span>
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {availableCategories.map(category => {
                          const isEnabled = categoryConfig[liquor.id]?.enabled_categories?.includes(category.id) || false;
                          
                          return (
                            <div 
                              key={category.id}
                              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                isEnabled 
                                  ? 'bg-green-50 border-green-300 text-green-800' 
                                  : 'bg-gray-50 border-gray-200 text-gray-600'
                              }`}
                              onClick={() => toggleCategory(liquor.id, category.id)}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{category.name}</span>
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                  isEnabled 
                                    ? 'bg-green-500 border-green-500' 
                                    : 'border-gray-300'
                                }`}>
                                  {isEnabled && (
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                              <p className="text-xs mt-1 opacity-75">
                                {isEnabled ? 'Categoria attiva' : 'Categoria disattivata'}
                              </p>
                            </div>
                          );
                        })}
                      </div>

                      {categoryConfig[liquor.id]?.updated_at && (
                        <p className="text-xs text-gray-500 mt-3">
                          Ultimo aggiornamento: {new Date(categoryConfig[liquor.id].updated_at).toLocaleString('it-IT')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminIngredients;